import React from 'react';
import styled, { type SupportedHTMLElements } from 'styled-components';
import type { ButtonVariant } from '../button/button.types';
import LinkBase from './link-base';
import forwardRef from '../../private/forwardRef';
import {
    LinkProps,
    LinkIconPlacement,
    LinkVariant,
    LinkAsButtonVariant,
    LinkSize,
} from './link.types';
import { NittyTheme } from '../../';
import ButtonBase from '../button/button-base';

// Equivalent of v6's KnownTarget (not re-exported from the package root):
// what the `as` prop accepts in an attrs return. `any` matches v6's own
// AnyComponent; props are contravariant, so `unknown` would reject every
// concrete component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalAsTarget = SupportedHTMLElements | React.ComponentType<any>;

type LinkWithButtonVariantProps = {
    $externalAs?: ExternalAsTarget;
};

const LinkWithButtonVariant = styled(
    ButtonBase,
).attrs<LinkWithButtonVariantProps>(({ $externalAs }) => ({
    as: $externalAs || 'a',
}))`
    text-decoration: none;
    display: flex;
    text-align: center;
    justify-content: center;
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
`;

type SpanWithMarginProps = {
    theme: NittyTheme;
    $iconPlacement: LinkIconPlacement;
    $buttonVariationMatches: boolean;
};

const SpanWithMargin = styled.span<SpanWithMarginProps>`
    ${({ $iconPlacement, $buttonVariationMatches, theme }) =>
        `margin-${$iconPlacement}: ${
            $buttonVariationMatches
                ? theme.spacing.extraSmall
                : theme.spacing.twoExtraSmall
        }`};
`;

const IconWrapper = styled.span`
    display: inline-flex;
    vertical-align: text-top;
`;

const defaultProps = {
    variant: 'primary' as LinkVariant | LinkAsButtonVariant,
    sizeVariant: 'large' as LinkSize,
    icon: null,
    hideLabel: false,
    iconPlacement: 'left' as LinkIconPlacement,
    fullWidth: false,
    compact: false,
};

const Link = forwardRef<LinkProps, 'a'>((props, ref) => {
    const {
        children,
        variant,
        icon,
        iconPlacement,
        hideLabel,
        sizeVariant,
        fullWidth,
        compact,
        dangerouslySetClassNames,
        as,
        ...rest
    } = { ...defaultProps, ...props };

    const buttonVariationMatches = variant.match(/button-(.*)/);
    // See Button: the regex capture re-widens the validated union to string.
    const variantName = (
        buttonVariationMatches ? buttonVariationMatches[1] : variant
    ) as LinkVariant;

    const renderIcon = () => {
        if (icon) {
            return buttonVariationMatches ? (
                icon
            ) : (
                <IconWrapper className={dangerouslySetClassNames?.iconWrapper}>
                    {icon}
                </IconWrapper>
            );
        }
    };

    const renderChildren = () =>
        icon ? (
            <SpanWithMargin
                $iconPlacement={iconPlacement}
                className={dangerouslySetClassNames?.label}
                $buttonVariationMatches={!!buttonVariationMatches}
            >
                {children}
            </SpanWithMargin>
        ) : (
            children
        );

    const commonProps = {
        // aria-label must be a string; a ReactNode child would read as
        // "[object Object]" to assistive tech.
        'aria-label':
            hideLabel && typeof children === 'string' ? children : undefined,
        $variant: variantName,
        $fullWidth: fullWidth,
        $compact: compact,
        $sizeVariant: sizeVariant,
        ref,
        ...rest,
    };

    const renderIconAndLabel = () => (
        <>
            {iconPlacement === 'left' && renderIcon()}
            {!hideLabel ? renderChildren() : null}
            {iconPlacement === 'right' && renderIcon()}
        </>
    );

    // The anchor-flavoured public API carries `type?: string` (a MIME hint),
    // which collides with the button-styled target's `type` union. The attrs
    // pin the rendered element to `as` (default 'a'), so the hint is
    // meaningless on this branch -- omit it rather than forward it.
    //
    // The remaining handler/ref types in `rest` are likewise
    // HTMLAnchorElement-flavoured while the styled target is a <button>; since
    // attrs guarantee the rendered element is the anchor, that mismatch is
    // type-level only. This is the one place the hand-rolled polymorphic
    // forwardRef wrapper meets v6's stricter target typing, and the cast is
    // deliberately confined to this single spread.
    const { type: _anchorTypeHint, ...buttonVariantProps } = commonProps;
    const buttonVariantSpread = buttonVariantProps as unknown as Omit<
        React.ComponentProps<typeof LinkWithButtonVariant>,
        '$variant' | '$externalAs'
    >;

    return buttonVariationMatches ? (
        // Mirror of Button: here variantName is the captured button-* variant.
        // `as` narrows from @nitty's ElementType-based As, which only differs
        // from ExternalAsTarget by SVG tag names.
        <LinkWithButtonVariant
            {...buttonVariantSpread}
            $externalAs={as as ExternalAsTarget}
            $variant={variantName as unknown as ButtonVariant}
        >
            {renderIconAndLabel()}
        </LinkWithButtonVariant>
    ) : (
        <LinkBase {...commonProps} as={as}>
            {renderIconAndLabel()}
        </LinkBase>
    );
});

Link.displayName = 'Link';

export default Link;
export { LinkBase };
